/**
 * MonthlyClosePage — Sec 13 of finance ERP spec.
 *
 * 25-item month-end closing checklist with:
 *   - Year/month picker
 *   - Progress bar (X / 25 done)
 *   - Cards (mobile) / table (desktop) per task
 *   - Status segmented control: Pending / In Progress / Done / Skipped
 *   - Notes + blocking-reason inputs
 *   - "Lock period" CTA (disabled until all 25 are DONE or SKIPPED)
 */
import { useState } from 'react'
import {
  Alert, App, Badge, Button, Card, Col, DatePicker, Empty, Grid, Input,
  Modal, Popconfirm, Progress, Row, Segmented, Select, Space, Spin, Tag,
  Typography,
} from 'antd'
import {
  CheckCircleOutlined, ClockCircleOutlined, LockOutlined,
  PlayCircleOutlined, PlusCircleOutlined, StopOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const { useBreakpoint } = Grid
const NUM_STYLE = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }

interface ChecklistItem {
  id: string
  sr_no: number
  activity: string
  responsible_role: string | null
  status: 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'SKIPPED'
  completed_by: string | null
  completed_at: string | null
  notes: string | null
  blocking_reason: string | null
}

interface MonthlyClose {
  id: string
  plant_id: string
  period_year: number
  period_month: number
  status: 'OPEN' | 'IN_PROGRESS' | 'CLOSED'
  closed_by: string | null
  closed_at: string | null
  notes: string | null
  created_at: string
  checklist_items: ChecklistItem[]
  progress_pct: number
  items_done: number
  items_total: number
}

const STATUS_OPTIONS = [
  { label: 'Pending',     value: 'PENDING' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'Done',        value: 'DONE' },
  { label: 'Skip',        value: 'SKIPPED' },
]

const STATUS_COLOR: Record<string, string> = {
  PENDING:     'default',
  IN_PROGRESS: 'processing',
  DONE:        'success',
  SKIPPED:     'warning',
}

const STATUS_ICON: Record<string, JSX.Element> = {
  PENDING:     <ClockCircleOutlined />,
  IN_PROGRESS: <PlayCircleOutlined />,
  DONE:        <CheckCircleOutlined />,
  SKIPPED:     <StopOutlined />,
}

export default function MonthlyClosePage() {
  const { message: msg } = App.useApp()
  const { plantId, plants, setPlantId } = usePlantId()
  const queryClient = useQueryClient()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [period, setPeriod] = useState<Dayjs>(dayjs().subtract(1, 'month'))
  const periodYear  = period.year()
  const periodMonth = period.month() + 1

  const closeQuery = useQuery<MonthlyClose | null>({
    queryKey: ['monthly-close', plantId, periodYear, periodMonth],
    queryFn: () => apiClient.get('/monthly-close', {
      params: { plant_id: plantId, period_year: periodYear, period_month: periodMonth },
    }).then(r => r.data),
    enabled: !!plantId,
  })

  const startMut = useMutation({
    mutationFn: () => apiClient.post('/monthly-close/start', {
      plant_id: plantId, period_year: periodYear, period_month: periodMonth,
    }).then(r => r.data),
    onSuccess: () => {
      msg.success('Closing run started — 25 tasks ready')
      queryClient.invalidateQueries({ queryKey: ['monthly-close'] })
    },
    onError: (e: any) => msg.error(e.response?.data?.detail || 'Failed to start'),
  })

  const updateMut = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: any }) =>
      apiClient.patch(`/monthly-close/items/${itemId}`, body).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['monthly-close'] }),
    onError: (e: any) => msg.error(e.response?.data?.detail || 'Update failed'),
  })

  const lockMut = useMutation({
    mutationFn: () => apiClient.post('/monthly-close/lock', {
      plant_id: plantId, period_year: periodYear, period_month: periodMonth,
    }).then(r => r.data),
    onSuccess: () => {
      msg.success('Period locked. No further postings without unlock.')
      queryClient.invalidateQueries({ queryKey: ['monthly-close'] })
    },
    onError: (e: any) => msg.error(e.response?.data?.detail || 'Lock failed — check pending items'),
  })

  const close = closeQuery.data
  const isLocked = close?.status === 'CLOSED'
  const allDone = close && close.items_done === close.items_total && close.items_total > 0

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <LockOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Month-End Closing
      </Title>
      <Text type="secondary">
        25-task closing checklist. Period is locked once every task is Done or Skipped.
      </Text>

      <Card style={{ marginTop: 16 }} styles={{ body: { padding: isMobile ? 12 : 24 } }}>
        {/* Period picker + plant selector */}
        <Row gutter={[8, 8]} align="middle" wrap>
          <Col xs={24} md="auto">
            <Select
              value={plantId} onChange={setPlantId} style={{ width: isMobile ? '100%' : 200 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col xs={24} md="auto">
            <DatePicker
              picker="month"
              value={period}
              onChange={v => v && setPeriod(v)}
              style={{ width: isMobile ? '100%' : 180 }}
              allowClear={false}
              disabledDate={d => d.isAfter(dayjs(), 'month')}
            />
          </Col>
          <Col flex="auto" />
          {!close && !closeQuery.isLoading && (
            <Col xs={24} md="auto">
              <Button
                type="primary" icon={<PlusCircleOutlined />} block={isMobile}
                size={isMobile ? 'large' : 'middle'}
                loading={startMut.isPending} onClick={() => startMut.mutate()}
              >
                Start Closing for {period.format('MMM YYYY')}
              </Button>
            </Col>
          )}
        </Row>

        {/* Loading / empty / progress */}
        {closeQuery.isLoading ? (
          <div style={{ marginTop: 24, textAlign: 'center' }}><Spin /></div>
        ) : !close ? (
          <Empty
            style={{ marginTop: 32 }}
            description={
              <Text type="secondary">
                No closing run started for {period.format('MMM YYYY')}.
                Click "Start Closing" above to seed the 25-task checklist.
              </Text>
            }
          />
        ) : (
          <>
            {/* Header banner */}
            <Card size="small" style={{ marginTop: 16,
                background: isLocked ? '#f6ffed' : '#fffbe6',
                borderColor: isLocked ? '#b7eb8f' : '#ffe58f' }}>
              <Row align="middle" justify="space-between" gutter={[12, 8]} wrap>
                <Col>
                  <Space size={12} wrap>
                    <Tag color={isLocked ? 'success' : close.status === 'IN_PROGRESS' ? 'processing' : 'default'}
                      style={{ fontSize: 13, padding: '4px 10px' }}>
                      {close.status}
                    </Tag>
                    <span style={{ fontSize: 14, fontWeight: 500, ...NUM_STYLE }}>
                      {close.items_done} / {close.items_total} tasks
                    </span>
                  </Space>
                </Col>
                <Col flex={isMobile ? '1 0 100%' : 'auto'}>
                  <Progress
                    percent={close.progress_pct}
                    status={isLocked ? 'success' : 'active'}
                    style={{ minWidth: isMobile ? '100%' : 240 }}
                  />
                </Col>
                <Col>
                  <Popconfirm
                    title="Lock this period?"
                    description="No journal entries will be allowed without an unlock by an admin."
                    onConfirm={() => lockMut.mutate()}
                    disabled={!allDone || isLocked}
                  >
                    <Button
                      type="primary"
                      icon={<LockOutlined />}
                      block={isMobile}
                      size={isMobile ? 'large' : 'middle'}
                      disabled={!allDone || isLocked}
                      loading={lockMut.isPending}
                    >
                      {isLocked ? 'Locked' : 'Lock Period'}
                    </Button>
                  </Popconfirm>
                </Col>
              </Row>
              {isLocked && close.closed_at && (
                <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                  Closed {dayjs(close.closed_at).format('DD MMM YYYY HH:mm')}
                </Text>
              )}
              {!allDone && !isLocked && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 10 }}
                  message="Some tasks pending"
                  description={`${close.items_total - close.items_done} items still need to be marked Done or Skipped before the period can be locked.`}
                />
              )}
            </Card>

            {/* Checklist — cards on mobile, table-like grid on desktop */}
            <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 16 }}>
              {close.checklist_items.map((item) => (
                <ChecklistRow
                  key={item.id}
                  item={item}
                  disabled={isLocked}
                  isMobile={isMobile}
                  onUpdate={(body) => updateMut.mutate({ itemId: item.id, body })}
                  saving={updateMut.isPending && updateMut.variables?.itemId === item.id}
                />
              ))}
            </Space>
          </>
        )}
      </Card>
    </div>
  )
}


// ─── Single checklist item row ───────────────────────────────────────────────
function ChecklistRow({
  item, disabled, isMobile, onUpdate, saving,
}: {
  item: ChecklistItem
  disabled: boolean
  isMobile: boolean
  onUpdate: (body: any) => void
  saving: boolean
}) {
  const [notesOpen, setNotesOpen] = useState(false)
  const [draftNotes, setDraftNotes] = useState(item.notes ?? '')
  const [draftBlocking, setDraftBlocking] = useState(item.blocking_reason ?? '')

  const isDone = item.status === 'DONE'
  const isSkipped = item.status === 'SKIPPED'
  const cardStyle =
    isDone ? { background: '#f6ffed', borderColor: '#b7eb8f' } :
    isSkipped ? { background: '#fff7e6', borderColor: '#ffd591' } :
    item.status === 'IN_PROGRESS' ? { background: '#e6f4ff', borderColor: '#91caff' } :
    {}

  return (
    <Card size="small" style={cardStyle} styles={{ body: { padding: isMobile ? 12 : 14 } }}>
      <Row gutter={[12, 8]} align="middle" wrap>
        <Col flex={isMobile ? 'none' : '40px'} style={{ minWidth: 40 }}>
          <Badge count={item.sr_no} showZero
            style={{ backgroundColor: isDone ? '#52c41a' : isSkipped ? '#faad14' : '#8c8c8c' }} />
        </Col>
        <Col flex="auto" style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, fontSize: isMobile ? 14 : 15 }}>
            {item.activity}
          </div>
          {item.responsible_role && (
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
              {item.responsible_role}
            </div>
          )}
          {(item.notes || item.blocking_reason) && (
            <div style={{ fontSize: 11, color: '#595959', marginTop: 4, fontStyle: 'italic' }}>
              {item.notes && <span>📝 {item.notes}</span>}
              {item.blocking_reason && <span style={{ color: '#cf1322', marginLeft: 8 }}>🚫 {item.blocking_reason}</span>}
            </div>
          )}
          {isDone && item.completed_at && (
            <div style={{ fontSize: 11, color: '#52c41a', marginTop: 2 }}>
              {STATUS_ICON.DONE} {dayjs(item.completed_at).format('DD MMM HH:mm')}
            </div>
          )}
        </Col>
        <Col xs={24} md="auto">
          <Space size={6} wrap>
            <Segmented
              size={isMobile ? 'middle' : 'small'}
              value={item.status}
              disabled={disabled || saving}
              options={STATUS_OPTIONS}
              onChange={(v) => onUpdate({ status: v })}
            />
            <Button
              type="text" size="small"
              disabled={disabled}
              onClick={() => {
                setDraftNotes(item.notes ?? '')
                setDraftBlocking(item.blocking_reason ?? '')
                setNotesOpen(true)
              }}
            >
              Notes
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Notes / blocking reason modal */}
      <Modal
        title={`Task #${item.sr_no} — Notes`}
        open={notesOpen}
        onCancel={() => setNotesOpen(false)}
        onOk={() => {
          onUpdate({
            notes: draftNotes || null,
            blocking_reason: draftBlocking || null,
          })
          setNotesOpen(false)
        }}
        okText="Save"
        width={isMobile ? '95%' : 520}
      >
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{item.activity}</Text>
        </div>
        <Input.TextArea
          rows={3}
          placeholder="Notes — what was done, references, attachments…"
          value={draftNotes}
          onChange={e => setDraftNotes(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Input.TextArea
          rows={2}
          placeholder="Blocking reason — only if status is Skipped or stuck"
          value={draftBlocking}
          onChange={e => setDraftBlocking(e.target.value)}
        />
      </Modal>
    </Card>
  )
}
